import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";

import { authSvcAxiosInstance } from "@/constants";
import { authServiceEndpoints } from "@/constants/endpoints";
import { PAGINATION } from "@/constants/pagination";
import { IUser } from "@/types/user.types";

// Query keys for better cache management
export const userKeys = {
    all: ["users"] as const,
    lists: () => [...userKeys.all, "list"] as const,
    list: (filters?: Record<string, unknown>) => [...userKeys.lists(), { filters }] as const,
    details: () => [...userKeys.all, "detail"] as const,
    detail: (id: string) => [...userKeys.details(), id] as const,
};

// Types for user service
type GetUsersOptions = {
    page?: number;
    limit?: number;
    search?: string;
    email?: string;
    permissionGroupId?: string;
};

type GetUsersResponse = {
    users: IUser[];
    total: number;
};

// API functions

const getUsersQuery = async (options: GetUsersOptions = {}): Promise<GetUsersResponse> => {
    const {
        page = PAGINATION.DEFAULT_PAGE,
        limit = PAGINATION.PAGE_SIZE,
        search = "",
        email = "",
        permissionGroupId = "",
    } = options;

    const response = await authSvcAxiosInstance.get(authServiceEndpoints.getUsers, {
        params: {
            search,
            email,
            permissionGroupId,
            page,
            limit,
        },
    });

    return {
        users: response.data.users,
        total: response.data.total,
    };
};

const getUsersByIdsQuery = async (ids: string[]): Promise<IUser[]> => {
    const response = await authSvcAxiosInstance.post(authServiceEndpoints.getUsersByIds, {
        userIds: ids,
    });
    return response.data;
};

const getUserQuery = async (userId: string): Promise<IUser> => {
    const response = await authSvcAxiosInstance.get(authServiceEndpoints.getUserById(userId));
    return response.data;
};

const getMeQuery = async (): Promise<IUser> => {
    const response = await authSvcAxiosInstance.get(authServiceEndpoints.getMe);
    return response.data;
};

const getUserByEmailQuery = async (email: string): Promise<IUser> => {
    const response = await authSvcAxiosInstance.get(authServiceEndpoints.getUserByEmail(email));
    return response.data;
};

// Query hooks
export const useUsers = (options: GetUsersOptions = {}, queryOptions?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: userKeys.list(options),
        queryFn: () => getUsersQuery(options),
        enabled: queryOptions?.enabled ?? true,
    });
};

export const useInfiniteUsers = (options: Omit<GetUsersOptions, "page"> = {}, queryOptions?: { enabled?: boolean }) => {
    const { limit = PAGINATION.PAGE_SIZE, ...rest } = options;

    return useInfiniteQuery<GetUsersResponse>({
        queryKey: [...userKeys.list(rest), limit],
        initialPageParam: 1,
        queryFn: ({ pageParam = 1 }) =>
            getUsersQuery({
                ...rest,
                page: Number(pageParam),
                limit,
            }),
        getNextPageParam: (lastPage, allPages) => {
            const loadedUsers = allPages.reduce((sum, page) => sum + page.users.length, 0);

            if (loadedUsers >= lastPage.total) {
                return undefined;
            }

            return allPages.length + 1;
        },
        enabled: queryOptions?.enabled ?? true,
    });
};

export const useUser = (userId?: string) => {
    return useQuery({
        queryKey: userKeys.detail(userId || ""),
        queryFn: () => getUserQuery(userId || ""),
        enabled: !!userId,
    });
};

export const useMe = () => {
    return useQuery({
        queryKey: userKeys.detail("me"),
        queryFn: getMeQuery,
    });
};

export const useUserByEmail = (email: string) => {
    return useQuery({
        queryKey: ["user", "byEmail", email],
        queryFn: () => getUserByEmailQuery(email),
        enabled: !!email,
    });
};

// Batch fetch users by IDs and return a map for quick lookup
export const useUsersByIds = (ids: string[]) => {
    const queries = useQueries({
        queries: ids.map((id) => ({
            queryKey: userKeys.detail(id),
            queryFn: () => getUserQuery(id),
            enabled: !!id,
        })),
    });

    const usersMap = (ids || []).reduce<Record<string, IUser>>((acc, id, idx) => {
        const data = queries[idx]?.data;
        if (data) acc[id] = data;
        return acc;
    }, {});

    return { usersMap, queries };
};

// Batch fetch users by IDs using the dedicated /user/batch endpoint
export const useUsersByIdsBatch = (ids: string[], enabled = true) => {
    return useQuery({
        queryKey: ["users", "batch", ids],
        queryFn: () => getUsersByIdsQuery(ids),
        enabled: enabled && ids.length > 0,
    });
};

export { getMeQuery, getUserByEmailQuery, getUserQuery, getUsersByIdsQuery, getUsersQuery };
